import type { IconType } from 'react-icons';
import {
  MdAccountTree,
  MdArticle,
  MdAttachMoney,
  MdDashboard,
  MdEco,
  MdEventBusy,
  MdFactCheck,
  MdGrass,
  MdInsights,
  MdLabel,
  MdStorefront,
  MdTimeline,
  MdTranslate,
} from 'react-icons/md';
import type { AdminRoleCode } from '@store/slices/auth';

export interface NavItem {
  label: string;
  icon: IconType;
  to: string;
  /** When true, item appears in the dashboard map but is non-navigable. */
  comingSoon?: boolean;
  /** One-line description shown on the dashboard module card. */
  description?: string;
  /**
   * Roles permitted to see this item. Omit for "every authenticated admin
   * panel user". ANY-of semantics — having one listed role is enough.
   *
   * Keep this aligned with the backend's `@RequireEntitlements` on the
   * matching controller. Backend rejects the API call regardless; this just
   * stops the user from seeing dead links.
   */
  allowedRoles?: AdminRoleCode[];
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

/**
 * Roles that get the full admin-panel surface. Used as the default
 * `allowedRoles` for items the business team should NOT see.
 */
const ADMIN_ONLY: AdminRoleCode[] = ['admin', 'tech_team'];

export const navConfig: NavSection[] = [
  {
    label: 'Workspace',
    items: [{ label: 'Home', icon: MdDashboard, to: '/home' }],
  },
  {
    label: 'Content',
    items: [
      {
        label: 'News articles',
        icon: MdArticle,
        to: '/news-articles',
        description: 'Long-form posts, drafts, and YouTube embeds.',
      },
      {
        label: 'Tags',
        icon: MdLabel,
        to: '/tags',
        description: 'Reusable labels applied to news articles.',
      },
    ],
  },
  {
    label: 'Pricing',
    items: [
      {
        label: 'Market prices',
        icon: MdAttachMoney,
        to: '/market-prices',
        description: 'Daily mandi observation prices for crops.',
      },
      {
        label: 'Price review',
        icon: MdFactCheck,
        to: '/price-review',
        description:
          'Review WhatsApp-ingested mandi prices before they publish.',
      },
      {
        label: 'Price series',
        icon: MdAccountTree,
        to: '/price-series',
        description: 'Switchboard of (APMC × crop × variety) tuples we track.',
      },
      {
        label: 'Forecasts',
        icon: MdInsights,
        to: '/forecasts',
        description: 'Model-driven price predictions per series · metric · horizon.',
      },
      {
        label: 'Forecast runs',
        icon: MdTimeline,
        to: '/forecasts/runs',
        description: 'Pipeline executions — status, timing, errors.',
        allowedRoles: ADMIN_ONLY,
      },
    ],
  },
  {
    label: 'Master data',
    items: [
      {
        label: 'Crops',
        icon: MdEco,
        to: '/crops',
        description: 'Master list of crops the platform tracks.',
      },
      {
        label: 'Varieties',
        icon: MdGrass,
        to: '/varieties',
        description: 'Crop sub-types tracked at each mandi.',
      },
      {
        label: 'APMCs',
        icon: MdStorefront,
        to: '/apmcs',
        description: 'Mandis (Agricultural Produce Market Committees) we track.',
      },
      {
        label: 'Market closures',
        icon: MdEventBusy,
        to: '/market-closures',
        description:
          'Holidays, strikes, and renovations — admin-curated downtime per APMC.',
      },
      {
        label: 'UI strings',
        icon: MdTranslate,
        to: '/ui-strings',
        description:
          'Mobile-app text by locale (sell tips, chip labels, trend captions). Edits propagate to mobile within 60s.',
      },
    ],
  },
];

/** True when the current user holds at least one of the item's allowed roles. */
function isItemVisible(item: NavItem, userRoles: readonly AdminRoleCode[]): boolean {
  if (!item.allowedRoles || item.allowedRoles.length === 0) return true;
  return userRoles.some((r) => item.allowedRoles!.includes(r));
}

export function findNavMatch(pathname: string): {
  section: NavSection;
  item: NavItem;
} | null {
  for (const section of navConfig) {
    for (const item of section.items) {
      if (item.to === pathname) return { section, item };
    }
  }
  return null;
}

/** Sidebar nav: built items only, with the Workspace section first, filtered by roles. */
export function getSidebarNav(userRoles: readonly AdminRoleCode[]): NavSection[] {
  return navConfig
    .map((s) => ({
      ...s,
      items: s.items.filter((i) => !i.comingSoon && isItemVisible(i, userRoles)),
    }))
    .filter((s) => s.items.length > 0);
}

/** Dashboard cards: every item except the Workspace section, filtered by roles. */
export function getDashboardModules(
  userRoles: readonly AdminRoleCode[],
): { section: NavSection; item: NavItem }[] {
  return navConfig
    .filter((s) => s.label !== 'Workspace')
    .flatMap((section) =>
      section.items
        .filter((item) => isItemVisible(item, userRoles))
        .map((item) => ({ section, item })),
    );
}
